import type { CssPosition } from '@ad-lucy/components/builderComponents/EnhancedStore/types';
import type { CreativesConfigComponentMetaConfig } from '@ad-lucy/config/creatives/types';
import type { ImageFocus } from '@ad-lucy/utils/assets';

type MetaConfig = {
  [key: string]: Record<string, CreativesConfigComponentMetaConfig>;
};

type EnumConfig = {
  [key: string]: {
    type: 'enum';
    oneOf: string[];
  };
};

export const META_TYPE = {
  STRING: 'string',
  COLOR_PICKER: 'color_picker',
  IMAGE: 'image',
  BOOLEAN: 'boolean',
  INTEGER: 'integer',
  TEXT: 'text',
  DATETIME: 'datetime',
  STICKY_POSITION: 'sticky_position',
  ENUM: 'enum',
  OBJECT: 'object',
  JSON: 'json',
  HTML: 'html',
  PRODUCT_ID: 'productId',
  RANGE: 'range',
  REFERENCE_ID: 'referenceId',
} as const;

export const metaConfigs = {
  navigation: {
    link: {
      type: META_TYPE.STRING,
    },
    internalReference: {
      type: META_TYPE.REFERENCE_ID,
    },
  },
  payouts: {
    clickPayout: {
      type: META_TYPE.BOOLEAN,
    },
    hoverPayout: {
      type: META_TYPE.BOOLEAN,
    },
  },
} as const satisfies MetaConfig;

export const enumConfigs = {
  cssPosition: {
    type: META_TYPE.ENUM,
    oneOf: ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'] as const satisfies CssPosition[],
  },
  focus: {
    type: META_TYPE.ENUM,
    oneOf: ['left', 'right', 'center'] as const satisfies ImageFocus[],
  },
  mobileInfoButtonIcon: {
    type: META_TYPE.ENUM,
    oneOf: ['plus', 'info'] as const,
  },
} as const satisfies EnumConfig;

export const MOBILE_INFO_BUTTON_ICON = {
  PLUS: 'plus',
  INFO: 'info',
} as const;

export type MobileInfoButtonIcon =
  (typeof MOBILE_INFO_BUTTON_ICON)[keyof typeof MOBILE_INFO_BUTTON_ICON];

export const DEFAULT_MOBILE_INFO_BUTTON_ICON = MOBILE_INFO_BUTTON_ICON.INFO;

export const countdownUnitVisibilityConfig = {
  type: META_TYPE.OBJECT,
  structure: {
    seconds: { type: META_TYPE.BOOLEAN },
    minutes: { type: META_TYPE.BOOLEAN },
    hours: { type: META_TYPE.BOOLEAN },
    days: { type: META_TYPE.BOOLEAN },
  },
} as const;

export const CREATIVE_TYPE = {
  AA: 'aa',
  BP: 'bp',
  CEAXL: 'ceaxl',
  SA: 'sa',
  VV: 'vv',
  BD: 'bd',
  RA: 'ra',
  ST: 'st',
} as const;

export type CreativeType = (typeof CREATIVE_TYPE)[keyof typeof CREATIVE_TYPE];

export const PRODUCT_TYPE = {
  AA_2: 'aa_2',
  BP_2: 'bp_2',
  CEAXL_2: 'ceaxl_2',
  SA_2: 'sa_2',
  RA: 'ra',
  ST: 'st',
  VV: 'vv',
  BD: 'bd',
} as const;

export type ProductType = (typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE];

export const TRANSITION_EFFECT = {
  FADE: 'fade',
  SLIDE: 'slide',
} as const;

export const LAYER_BUTTON_POSITION = {
  TOP_LEFT: 'topLeft',
  TOP_RIGHT: 'topRight',
  TOP_CENTER: 'topCenter',
  BOTTOM_LEFT: 'bottomLeft',
  BOTTOM_RIGHT: 'bottomRight',
  BOTTOM_CENTER: 'bottomCenter',
} as const;

export type LayerButtonPosition =
  (typeof LAYER_BUTTON_POSITION)[keyof typeof LAYER_BUTTON_POSITION];

export const VIDEO_POSITION = {
  TOP: 'top',
  BOTTOM: 'bottom',
} as const;

export type VideoPosition = (typeof VIDEO_POSITION)[keyof typeof VIDEO_POSITION];

export const VIDEO_CONTROLS_PLACEMENT = {
  OUTSIDE_VIDEO: 'outsideVideo',
  INSIDE_VIDEO: 'insideVideo',
} as const;

export type VideoControlsPlacement =
  (typeof VIDEO_CONTROLS_PLACEMENT)[keyof typeof VIDEO_CONTROLS_PLACEMENT];

export const PLAYER_CONTROL_TYPE = {
  STANDARD: 'standard',
  COUNTDOWN: 'Countdown',
} as const;

export type PlayerControlType = (typeof PLAYER_CONTROL_TYPE)[keyof typeof PLAYER_CONTROL_TYPE];

export const SLIDER_DESIGN = {
  TILE: 'tile',
  SLIDER: 'slider',
} as const;

export type SliderDesign = (typeof SLIDER_DESIGN)[keyof typeof SLIDER_DESIGN];

export const NAVIGATION_ARROW_TYPE = {
  SIDE_CONTROL: 'sideControl',
  CENTER_DEFAULT_CONTROL: 'centerDefaultControl',
  CENTER_ROUNDED_CONTROL: 'centerRoundedControl',
} as const;

export type NavigationArrowType =
  (typeof NAVIGATION_ARROW_TYPE)[keyof typeof NAVIGATION_ARROW_TYPE];

export const NAVIGATION_ARROW_PULSE_ANIMATION = {
  ALWAYS: 'always',
  HOVER: 'hover',
  NONE: 'none',
} as const;

export type NavigationArrowPulseAnimation =
  (typeof NAVIGATION_ARROW_PULSE_ANIMATION)[keyof typeof NAVIGATION_ARROW_PULSE_ANIMATION];

export type BackgroundImageFocus = 'left' | 'right' | 'center';

export type BreadcrumbAlignment = 'left' | 'right' | 'center';

export type BreadcrumbTextTransform = 'uppercase' | 'none';

export const IMAGE_ANIMATION_TYPE = {
  ZOOM_IN: 'zoomIn',
  ZOOM_OUT: 'zoomOut',
  SLIDE: 'slide',
  FADE_IN: 'fadeIn',
} as const;

export const STICKY_CTA_ANIMATION_TYPE = {
  PULSE: 'pulse',
  PULSE_GROW: 'pulseGrow',
  BOUNCE_IN: 'bounceIn',
  WOBBLE_HORIZONTAL: 'wobbleHorizontal',
  WOBBLE_VERTICAL: 'wobbleVertical',
  BUZZ: 'buzz',
} as const;

export const BACKGROUND_TRANSITION_TYPE = {
  FADE: 'fade',
  SWEEP_TO_RIGHT: 'sweepToRight',
  RECTANGLE_OUT: 'rectangleOut',
  SHUTTER_OUT_VERTICAL: 'shutterOutVertical',
} as const;

export const ANIMATION_ITERATION_TYPE = {
  ON_HOVER: 'onHover',
  ALWAYS: 'always',
} as const;

export const SWIPER_DIRECTION = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
} as const;

export const SLIDE_DIRECTION = {
  IN: 'in',
  OUT: 'out',
} as const;

export const NAVIGATION_DIRECTION = {
  PREV: 'prev',
  NEXT: 'next',
} as const;

export const POSITION = {
  LEFT: 'left',
  RIGHT: 'right',
  TOP: 'top',
  BOTTOM: 'bottom',
  CENTER: 'center',
} as const;

export const IMAGE_COMPARATOR_DESIGN = {
  ROUND: 'round',
  ELONGATED: 'elongated',
} as const;

export const IMAGE_THEME = {
  DARK: 'dark',
  LIGHT: 'light',
} as const;

export const STORE_FINDER_TEMPLATE = {
  BASIC: 'basic',
  CALENDAR: 'calendar',
  EUROPE: 'europe',
} as const;

export const VIDEO_CONTROLS_VISIBILITY = {
  ALL: 'all',
  MUTE_ONLY: 'muteOnly',
  PLAY_PAUSE_ONLY: 'playPauseOnly',
} as const;

export const HOVER_TARGET = {
  TILE: 'tile',
  COMPONENT: 'component',
} as const;
